import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, MailCheck, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIVISIONS = [
  { value: "academy",       label: "Academy" },
  { value: "study_abroad",  label: "Study Abroad" },
  { value: "mbbs_abroad",   label: "MBBS Abroad" },
  { value: "wealth",        label: "Wealth" },
  { value: "digital",       label: "Digital" },
];

const ROLE_HINTS = [
  "Academy Counsellor",
  "Study Abroad Counsellor",
  "Wealth Executive",
  "Digital Project Manager",
  "Franchise Manager",
  "Business Consultant",
  "Operations",
  "HR",
  "Finance / Accounts",
  "Other",
];

function scorePassword(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

export default function RequestAccess() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    employee_id: "", job_title: "", department: "",
    division: "", reporting_manager: "", joining_date: "",
    role_requested: "",
    password: "", password2: "",
    reason: "",
    consent: false, terms_ack: false, website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resending, setResending] = useState(false);

  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const strength = useMemo(() => scorePassword(f.password), [f.password]);
  const strengthLabel = ["Very weak", "Weak", "Fair", "Good", "Strong", "Excellent"][strength];
  const pwMatch = f.password && f.password === f.password2;

  const canSubmit =
    f.name.trim() && f.email.trim() && f.phone.trim() &&
    f.consent && f.terms_ack &&
    f.password.length >= 8 && strength >= 3 && pwMatch && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent || !f.terms_ack) return toast.error("Please accept both consent and terms.");
    if (!pwMatch) return toast.error("Passwords do not match");
    if (strength < 3) return toast.error("Please choose a stronger password (upper/lower/digit/symbol).");
    setSubmitting(true);
    try {
      const body = {
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim(),
        employee_id: f.employee_id.trim() || undefined,
        job_title: f.job_title.trim() || undefined,
        department: f.department.trim() || undefined,
        division: f.division || undefined,
        reporting_manager: f.reporting_manager.trim() || undefined,
        joining_date: f.joining_date || undefined,
        role_requested: f.role_requested.trim() || undefined,
        reason: f.reason.trim() || undefined,
        password: f.password,
        consent: f.consent,
        terms_ack: f.terms_ack,
        page_url: typeof window !== "undefined" ? window.location.href : undefined,
      };
      await axios.post(`${API}/public/access-requests`, body);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async () => {
    if (!f.email.trim()) return toast.error("Enter your email first");
    setResending(true);
    try {
      await axios.post(`${API}/public/resend-verification`, { email: f.email.trim() });
      toast.success("If your request is on file, a fresh verification email has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend. Please try again.");
    } finally { setResending(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
           style={{ background: "var(--miro-bg, #F5F7FB)" }}>
        <div data-testid="request-access-success"
             className="max-w-md w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
               style={{ background: "var(--miro-navy)" }}>
            <MailCheck className="text-white" size={26}/>
          </div>
          <h1 className="font-display font-black text-2xl text-[var(--miro-navy)]">Verify your email</h1>
          <p className="text-sm text-[var(--miro-text-secondary)] mt-3">
            We've sent a verification link to
            <strong className="text-[var(--miro-navy)]"> {f.email}</strong>. Please click
            it within <strong>30 minutes</strong> to move your request to admin
            review.
          </p>
          <p className="text-xs text-[var(--miro-text-muted)] mt-4">
            After verification, MIRO Support reviews your request. You'll get a
            second email once your account is activated. Then sign in with the
            email and password you chose here.
          </p>
          <div className="flex gap-2 mt-6">
            <Button
              data-testid="request-access-resend-btn"
              variant="outline"
              onClick={resend}
              disabled={resending}
              className="flex-1 h-11 font-semibold"
            >
              {resending ? "Resending…" : "Resend verification email"}
            </Button>
            <Button
              data-testid="request-access-back-btn"
              onClick={() => nav("/login")}
              className="flex-1 h-11 font-semibold"
              style={{ background: "var(--miro-navy)" }}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{ background: "var(--miro-bg, #F5F7FB)" }}>
      <div className="max-w-2xl w-full bg-white border border-[var(--miro-border)] rounded-2xl p-8">
        <Link to="/login" data-testid="request-access-back-link"
              className="inline-flex items-center gap-1 text-xs text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)]">
          <ArrowLeft size={13}/> Back to sign in
        </Link>
        <div className="miro-overline mt-4">Request Employee Access</div>
        <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
          Sign up for MIRO One
        </h1>
        <p className="text-sm text-[var(--miro-text-secondary)] mt-2">
          Public registration is a <em>request</em> only — you'll be able to
          sign in once MIRO Support reviews your details and activates your
          account. You'll receive an email verification link and then a
          notification when your account is approved.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          {/* Honeypot */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={f.website}
            onChange={(e) => upd("website", e.target.value)}
            style={{ position: "absolute", left: "-10000px", opacity: 0, height: 0, width: 0 }}
            aria-hidden="true"
          />

          {/* Contact */}
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Full name *</Label>
              <Input data-testid="req-access-name" required className="mt-1"
                     value={f.name} onChange={(e) => upd("name", e.target.value)}/>
            </div>
            <div>
              <Label>Work email *</Label>
              <Input data-testid="req-access-email" required type="email" className="mt-1"
                     value={f.email} onChange={(e) => upd("email", e.target.value)}/>
            </div>
            <div>
              <Label>Phone / WhatsApp *</Label>
              <Input data-testid="req-access-phone" required className="mt-1"
                     placeholder="+91…" value={f.phone} onChange={(e) => upd("phone", e.target.value)}/>
            </div>
            <div>
              <Label>Employee ID (if any)</Label>
              <Input data-testid="req-access-employee-id" className="mt-1"
                     value={f.employee_id} onChange={(e) => upd("employee_id", e.target.value)}/>
            </div>
          </fieldset>

          {/* Employment */}
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Job title</Label>
              <Input data-testid="req-access-job-title" className="mt-1"
                     value={f.job_title} onChange={(e) => upd("job_title", e.target.value)}/>
            </div>
            <div>
              <Label>Department</Label>
              <Input data-testid="req-access-department" className="mt-1"
                     value={f.department} onChange={(e) => upd("department", e.target.value)}/>
            </div>
            <div>
              <Label>Preferred division</Label>
              <Select value={f.division} onValueChange={(v) => upd("division", v)}>
                <SelectTrigger data-testid="req-access-division" className="mt-1">
                  <SelectValue placeholder="Select division"/>
                </SelectTrigger>
                <SelectContent>
                  {DIVISIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reporting manager (if known)</Label>
              <Input data-testid="req-access-manager" className="mt-1"
                     value={f.reporting_manager} onChange={(e) => upd("reporting_manager", e.target.value)}/>
            </div>
            <div>
              <Label>Role you'd use</Label>
              <Input list="ra-roles" data-testid="req-access-role" className="mt-1"
                     value={f.role_requested} onChange={(e) => upd("role_requested", e.target.value)}
                     placeholder="e.g. Academy Counsellor"/>
              <datalist id="ra-roles">
                {ROLE_HINTS.map((r) => <option key={r} value={r}/>)}
              </datalist>
              <div className="text-[11px] text-[var(--miro-text-muted)] mt-1">
                Privileged roles (Founder / Operations / HR / Finance / Franchise / Super Admin) are only granted by admin during review.
              </div>
            </div>
            <div>
              <Label>Joining date</Label>
              <Input data-testid="req-access-joining-date" type="date" className="mt-1"
                     value={f.joining_date} onChange={(e) => upd("joining_date", e.target.value)}/>
            </div>
          </fieldset>

          {/* Password */}
          <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Choose a password *</Label>
              <Input data-testid="req-access-password" type="password" required minLength={8} className="mt-1"
                     value={f.password} onChange={(e) => upd("password", e.target.value)}
                     placeholder="Min 8 chars · upper/lower/digit/symbol"/>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${(strength / 5) * 100}%`,
                      background: strength < 3 ? "#ef4444" : strength < 4 ? "#f59e0b" : "#10b981",
                    }}
                  />
                </div>
                <span className="text-[10px] text-[var(--miro-text-muted)]">{strengthLabel}</span>
              </div>
            </div>
            <div>
              <Label>Confirm password *</Label>
              <Input data-testid="req-access-password2" type="password" required className="mt-1"
                     value={f.password2} onChange={(e) => upd("password2", e.target.value)}/>
              {f.password2 && !pwMatch && (
                <div className="mt-1 text-[11px] text-red-600">Passwords don't match yet.</div>
              )}
            </div>
          </fieldset>

          <div>
            <Label>Reason for requesting access</Label>
            <Textarea data-testid="req-access-reason" rows={3} className="mt-1"
                      value={f.reason} onChange={(e) => upd("reason", e.target.value)}
                      placeholder="How will you use MIRO One? Team, referral, use-case…"/>
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 text-xs text-[var(--miro-text-secondary)]">
              <input data-testid="req-access-consent" type="checkbox" className="mt-0.5"
                     checked={f.consent} onChange={(e) => upd("consent", e.target.checked)}/>
              <span>I confirm the details above are accurate and consent to MIRO Consultancy contacting me about this account.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[var(--miro-text-secondary)]">
              <input data-testid="req-access-terms" type="checkbox" className="mt-0.5"
                     checked={f.terms_ack} onChange={(e) => upd("terms_ack", e.target.checked)}/>
              <span>I have read and accept the MIRO One terms of use and privacy policy.</span>
            </label>
          </div>

          <Button
            data-testid="req-access-submit"
            type="submit" disabled={!canSubmit}
            className="w-full h-11 font-semibold"
            style={{ background: "var(--miro-navy)" }}
          >
            {submitting ? "Submitting…" : (<><UserPlus size={15} className="mr-1.5"/> Create account</>)}
          </Button>
        </form>

        <div className="mt-6 text-[11px] text-[var(--miro-text-muted)] leading-relaxed">
          Already have an account? <Link to="/login" className="text-[var(--miro-blue)] font-semibold">Sign in</Link>.
          Forgot your password? <Link to="/forgot-password" className="text-[var(--miro-blue)] font-semibold">Reset it</Link>.
        </div>
      </div>
    </div>
  );
}
